import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {FormsModule} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {Subject, Subscription} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap, tap} from 'rxjs/operators';
import {TicketingService} from "../../services/ticketing.service";

export interface Organizer {
  id: string;
  _id?: string;
  name?: string;
  email?: string;
}

export interface Event {
  id: string;
  name?: string;
  title?: string;
}

export interface ImpersonateDialogData {
  email: string;
  organizer?: string;
  organizerName?: string;
}

export interface ImpersonateDialogDataReturn {
  email: string;
  organizer?: string;
  organizerName?: string;
}

@Component({
  selector: 'app-impersonate-dialog',
  standalone: true,
  imports: [
    MatDialogModule,
    FormsModule,
    CommonModule
  ],
  template: `
    <div (click)="onDocumentClick($event)" style="overflow: visible;">
      <h2 mat-dialog-title class="ui-confirm__title">Impersonate User</h2>
      <div class="ui-confirm__body" style="overflow: visible;">
        <p>Select organizer and event for impersonation:</p>

        <!-- Organizer Selection with Autocomplete -->
        <div class="ui-form-group">
          <label class="ui-form-label">Organizer</label>
          <div class="autocomplete-container" (click)="onDropdownClick($event)">
            <input
              #searchInput
              class="boc-input"
              [(ngModel)]="organizerSearchTerm"
              (input)="onOrganizerSearchChange()"
              (focus)="onOrganizerFocus()"
              (keydown)="onKeyDown($event)"
              placeholder="Type to search organizer (min. 2 characters)..."
            />
            <div class="dropdown-list" *ngIf="showOrganizerDropdown" [style.top.px]="dropdownTop" [style.left.px]="dropdownLeft" [style.width.px]="dropdownWidth">
              <div class="dropdown-info" *ngIf="showSearchHint">
                Type at least 2 characters to search
              </div>
              <div class="dropdown-info" *ngIf="showTooManyResults">
                Too many results ({{ filteredOrganizers.length }}). Showing first {{ maxDisplayedOrganizers }}. Please refine your search.
              </div>
              <div
                class="dropdown-item"
                *ngFor="let organizer of displayedOrganizers; let i = index"
                [class.selected]="i === selectedIndex"
                (click)="selectOrganizer(organizer)"
                (mouseenter)="selectedIndex = i"
              >
                {{ organizer.name || organizer.email || organizer.id || organizer._id }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="ui-confirm__footer">
        <button class="ui-btn ui-btn--secondary ui-btn--sm ui-btn--auto" (click)="onCancel()">Cancel</button>
        <button class="ui-btn ui-btn--primary ui-btn--sm ui-btn--auto" (click)="onConfirm()" [disabled]="!data.organizer">
          Impersonate
        </button>
      </div>
    </div>
  `,
  styles: [`
    /* Dialog body */
    :host ::ng-deep .ui-confirm__body {
      min-width: 300px;
      overflow: visible;
    }

    /* Form group override for overflow */
    .ui-form-group {
      position: relative;
      overflow: visible;
    }

    /* Autocomplete container styling */
    .autocomplete-container {
      position: relative;
      width: 100%;
      box-sizing: border-box;
    }

    /* Dropdown list styling */
    .dropdown-list {
      position: fixed;
      background-color: var(--ui-color-bg);
      border: var(--ui-border-width) solid var(--ui-color-border);
      border-top: none;
      border-radius: 0 0 var(--ui-border-radius) var(--ui-border-radius);
      max-height: 300px;
      z-index: var(--ui-zIndex-modal);
      box-shadow: var(--ui-shadow-lg);
      box-sizing: border-box;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .dropdown-list > * {
      flex-shrink: 0;
    }

    /* Scrollable wrapper inside dropdown */
    .dropdown-list {
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: var(--ui-color-neutral-400) var(--ui-color-neutral-100);
    }

    .dropdown-list::-webkit-scrollbar {
      width: 10px;
    }

    .dropdown-list::-webkit-scrollbar-track {
      background: var(--ui-color-neutral-100);
    }

    .dropdown-list::-webkit-scrollbar-thumb {
      background: var(--ui-color-neutral-400);
      border-radius: 5px;
      border: 2px solid var(--ui-color-neutral-100);
    }

    .dropdown-list::-webkit-scrollbar-thumb:hover {
      background: var(--ui-color-neutral-600);
    }

    /* Dropdown item styling */
    .dropdown-item {
      padding: 10px 12px;
      cursor: pointer;
      border-bottom: var(--ui-border-width) solid var(--ui-color-border-muted);
      transition: background-color 0.2s ease;
      color: var(--ui-color-text);
    }

    .dropdown-item:hover {
      background-color: var(--ui-color-bg-secondary);
    }

    .dropdown-item.selected {
      background-color: var(--ui-color-primary-lightest);
      border-left: 3px solid var(--ui-color-primary);
    }

    .dropdown-item:last-child {
      border-bottom: none;
    }

    /* Dropdown info message styling */
    .dropdown-info {
      padding: 10px 12px;
      background-color: var(--ui-color-info-light);
      color: var(--ui-color-info-dark);
      font-size: var(--ui-font-size-sm);
      border-bottom: var(--ui-border-width) solid var(--ui-color-border-muted);
      text-align: center;
    }

    /* Input focus state when dropdown is open */
    .autocomplete-container .boc-input:focus {
      border-radius: var(--ui-border-radius-md) var(--ui-border-radius-md) 0 0;
    }

    .autocomplete-container .boc-input:focus + .dropdown-list {
      border-top: var(--ui-border-width) solid var(--ui-color-border-focus);
    }
  `]
})
export class ImpersonateDialogComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;

  filteredOrganizers: Organizer[] = [];
  displayedOrganizers: Organizer[] = [];
  organizerSearchTerm: string | undefined = '';
  showOrganizerDropdown: boolean = false;
  showSearchHint: boolean = false;
  showTooManyResults: boolean = false;
  loadingOrganizers: boolean = false;
  maxDisplayedOrganizers: number = 50;
  minSearchLength: number = 2;
  selectedIndex: number = 0;
  dropdownTop: number = 0;
  dropdownLeft: number = 0;
  dropdownWidth: number = 0;

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private readonly DEBOUNCE_TIME = 300;

  constructor(
    public dialogRef: MatDialogRef<ImpersonateDialogComponent, ImpersonateDialogDataReturn>,
    @Inject(MAT_DIALOG_DATA) public data: ImpersonateDialogData,
    private apiTicketingService: TicketingService,
  ) {
  }

  ngOnInit(): void {
    this.setupSearchDebounce();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  private setupSearchDebounce(): void {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(this.DEBOUNCE_TIME),
      distinctUntilChanged(),
      tap(() => {
        this.loadingOrganizers = true;
      }),
      switchMap(searchTerm => {
        const partnerId = localStorage.getItem('impersonateRole') === 'partner'
          ? localStorage.getItem('originalUserEmail') || undefined
          : undefined;
        return this.apiTicketingService.searchOrganizers(searchTerm, partnerId);
      })
    ).subscribe({
      next: (response: any) => {
        this.filteredOrganizers = response || [];
        this.updateDisplayedOrganizers();
        this.loadingOrganizers = false;
        this.showOrganizerDropdown = true;
        this.showSearchHint = false;
      },
      error: (error) => {
        console.error('Error searching organizers:', error);
        this.loadingOrganizers = false;
        this.filteredOrganizers = [];
        this.displayedOrganizers = [];
      }
    });
  }

  private updateDisplayedOrganizers(): void {
    if (this.filteredOrganizers.length > this.maxDisplayedOrganizers) {
      this.showTooManyResults = true;
      this.displayedOrganizers = this.filteredOrganizers.slice(0, this.maxDisplayedOrganizers);
    } else {
      this.showTooManyResults = false;
      this.displayedOrganizers = [...this.filteredOrganizers];
    }
    this.selectedIndex = 0;
  }

  onOrganizerFocus(): void {
    this.updateDropdownPosition();
    if (this.filteredOrganizers.length > 0) {
      this.showOrganizerDropdown = true;
    } else if (this.organizerSearchTerm && this.organizerSearchTerm.length >= this.minSearchLength) {
      this.showOrganizerDropdown = true;
    } else {
      this.showSearchHint = true;
      this.showOrganizerDropdown = true;
    }
  }

  onOrganizerSearchChange(): void {
    this.updateDropdownPosition();
    const searchTerm = this.organizerSearchTerm?.trim() || '';

    if (searchTerm.length < this.minSearchLength) {
      this.showSearchHint = true;
      this.showTooManyResults = false;
      this.filteredOrganizers = [];
      this.displayedOrganizers = [];
      this.showOrganizerDropdown = searchTerm.length > 0;
      return;
    }

    this.showSearchHint = false;
    this.searchSubject.next(searchTerm);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.showOrganizerDropdown || this.displayedOrganizers.length === 0) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, this.displayedOrganizers.length - 1);
        this.scrollToSelected();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        this.scrollToSelected();
        break;

      case 'Enter':
        event.preventDefault();
        if (this.displayedOrganizers[this.selectedIndex]) {
          this.selectOrganizer(this.displayedOrganizers[this.selectedIndex]);
          this.onConfirm();
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.showOrganizerDropdown = false;
        break;
    }
  }

  selectOrganizer(organizer: Organizer): void {
    // Store the ID (email) for authentication, display the name for UX
    this.data.organizer = organizer.id || organizer._id || organizer.email;
    this.data.organizerName = organizer.name || organizer.email || organizer.id || organizer._id;
    this.organizerSearchTerm = this.data.organizerName;
    this.showOrganizerDropdown = false;
    this.showSearchHint = false;
    this.showTooManyResults = false;
  }

  private updateDropdownPosition(): void {
    setTimeout(() => {
      if (this.searchInput && this.searchInput.nativeElement) {
        const rect = this.searchInput.nativeElement.getBoundingClientRect();
        this.dropdownTop = rect.bottom;
        this.dropdownLeft = rect.left;
        this.dropdownWidth = rect.width;
      }
    }, 0);
  }

  private scrollToSelected(): void {
    setTimeout(() => {
      const dropdownList = document.querySelector('.dropdown-list') as HTMLElement;
      const selectedItem = document.querySelector('.dropdown-item.selected') as HTMLElement;

      if (dropdownList && selectedItem) {
        const containerTop = dropdownList.scrollTop;
        const containerBottom = containerTop + dropdownList.clientHeight;
        const itemTop = selectedItem.offsetTop;
        const itemBottom = itemTop + selectedItem.offsetHeight;

        if (itemBottom > containerBottom) {
          // Scroll just enough to show the item (incremental)
          dropdownList.scrollTop = containerTop + (itemBottom - containerBottom);
        } else if (itemTop < containerTop) {
          // Scroll just enough to show the item (incremental)
          dropdownList.scrollTop = itemTop;
        }
      }
    }, 0);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.data.organizer) {
      this.dialogRef.close({
        organizer: this.data.organizer,
        organizerName: this.data.organizerName,
        email: this.data.email?.trim()
      });
    }
  }

  // Handle clicking outside dropdowns to close them
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.autocomplete-container')) {
      this.showOrganizerDropdown = false;
    }
  }

  // Prevent dropdown from closing when clicking inside
  onDropdownClick(event: MouseEvent): void {
    event.stopPropagation();
  }
}
